import React, { useEffect, useRef, useState } from 'react';
import { HeaderLayout, HeaderLayoutProps } from '../../UI/atoms/header/Header';
import { FooterLayout } from '../../UI/atoms/footer/Footer';
import styled from 'styled-components';
import { getAccountConnectToken } from '@services/AccountConnectFormService';
import { auth, config } from '../../../config';
import { clearCookie } from '@utils/cookieHelper';
import { SSOProps } from '@types';
import { isEmpty } from 'lodash';
import AccConnectFormSubmit from '@components/AccountConnectFormSubmit/AccConnectFormSubmit';
import { HomeContext } from '@components_v2/context/MyHomeContext';
import { useIsFetching } from '@tanstack/react-query';
import { AuthenticationLoader } from '@components_v2/UI/atoms/loading';
import { dispatch } from '@store';
import { clearStatementError } from '@store/actions/statements.action';
import { useSelector } from 'react-redux';
import { selectStatementError, selectStatementLoading } from '@store/selectors/statement.selector';
import { enqueueSnackbar } from 'notistack';
import { UIConstants } from '@constants';
import Loader from '@components/Loader/Loader';
import { NativeBaseProvider } from 'native-base';
import { useNavigate } from 'react-router-dom';
import { useSnackBarProps } from '@hooks/useSnackbarProps';
import { VWOContextProvider } from '../../../../src/context/VWOContext';

export type WithHeaderFooterArgs = {
    i18n?: any;
} & HeaderLayoutProps;

export const StyledFooter = styled.footer``;

export const StyledHeader = styled.header``;
export const StyledMain = styled.main`
    flex-grow: 1;
    flex-shrink: 0;
    flex-basis: auto;
`;

export const withHeaderFooter = (
    WrappedComponent: any,
    { i18n, showMenuOptions, showDropDownOptions, BreadCrumbs, title, showFooterAds, optionalMenu, useExitIntent }: WithHeaderFooterArgs
) => {
    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

    const ComponentWithHeaderFooter = (props: any) => {
        const [loggedOut, setLogout] = React.useState(false);

        const showLoader = useSelector(selectStatementLoading);
        const errorDetails = useSelector(selectStatementError);
        const isFetching = useIsFetching({
            queryKey: ['widgets', 'new-loan', 'notifications', 'auto-pay', 'payment-history', 'accounts', 'home']
        });

        const navigate = useNavigate();
        const snackProps = useSnackBarProps({ severity: 'error' });
        const [docType, setDocType] = useState('');

        const ref = useRef<null | { triggerEvent: (name: any) => void }>();
        const [loading, setLoading] = useState(false);
        const [ssoData, setSsoData] = useState<SSOProps>({
            url: '',
            token: ''
        });
        const updateSsoData = ({ url, token }: { url: string; token: string }) => {
            setSsoData({
                url,
                token
            });
        };

        const accountDetailSSO = async (url: string) => {
            setLoading(true);
            try {
                const { response, error } = await getAccountConnectToken(`${config.env('ssoUrl')}`, `${config.env('xApiKey')}`);

                if (response) {
                    auth.getOkta().tokenManager.clear();
                    clearCookie();
                    updateSsoData({ url, token: response?.signinToken });
                } else navigate('error');
            } catch (e) {
                console.log({ e });
            } finally {
                setLoading(false);
            }
        };

        const onHeaderClickHandler = (name: any) => {
            if (ref && ref.current) ref.current.triggerEvent(name);
        };

        const onLogoutEvent = () => {
            setLogout(true);
        };
        const docRedirect = () => {
            window.open(config.env('redirectUri') + '#/document');
        };

        useEffect(() => {
            if (errorDetails) {
                enqueueSnackbar(UIConstants.errorDetails, snackProps);
                dispatch(clearStatementError());
            }
        }, [errorDetails]);

        if (loading || loggedOut) return <AuthenticationLoader />;
        if (!isEmpty(ssoData.url) && !isEmpty(ssoData.token))
            return <AccConnectFormSubmit method="post" action={ssoData.url} value={ssoData.token} />;
        return (
            <NativeBaseProvider>
                <VWOContextProvider>
                    <HomeContext.Provider value={{ accountDetailSSO, onLogoutEvent }}>
                        <Loader showLoader={showLoader} />
                        <StyledHeader>
                            <HeaderLayout
                                {...{
                                    showMenuOptions,
                                    showDropDownOptions,
                                    BreadCrumbs,
                                    title,
                                    optionalMenu,
                                    useExitIntent,
                                    onHeaderClickHandler
                                }}
                            />
                        </StyledHeader>
                        <StyledMain>
                            <WrappedComponent {...props} ref={ref} />
                        </StyledMain>
                        <StyledFooter>
                            <FooterLayout {...{ showFooterAds }} />
                        </StyledFooter>
                    </HomeContext.Provider>
                </VWOContextProvider>
            </NativeBaseProvider>
        );
    };
    ComponentWithHeaderFooter.displayName = `withHeaderFooter(${displayName})`;

    return ComponentWithHeaderFooter;
};
